import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '../ui/Button';
import { EstadoError, EstadoVacio, ListaEsqueleto } from '../ui/Estados';
import { Input, Select, type OpcionSelect } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { Tabla, type Columna } from '../ui/Tabla';
import { BarraFiltros } from './BarraFiltros';
import { CabeceraPagina } from './CabeceraPagina';
import { FichaRegistro } from './FichaRegistro';
import { CampoCompleto, RejillaCampos } from './RejillaCampos';
import {
  repositorioInventario,
  type FilaInventario,
  type NombreEntidad,
} from '../../../data/repositories/RepositorioInventario';
import { ErrorApp, registrarError } from '../../../domain/errores';
import { useSesionStore } from '../../stores/sesionStore';
import estilos from './PaginaCrud.module.css';

/** Campo del formulario, declarado en vez de escrito a mano en cada pantalla. */
export interface CampoFormulario {
  clave: string;
  etiqueta: string;
  tipo: 'texto' | 'numero' | 'select' | 'checkbox';
  requerido?: boolean;
  ayuda?: string;
  placeholder?: string;
  /** Para los desplegables; puede depender de los valores ya introducidos. */
  opciones?: readonly OpcionSelect[] | ((valores: FilaInventario) => readonly OpcionSelect[]);
  /** Impide editarlo una vez creado el registro. */
  soloAlCrear?: boolean;
  /** Tope de caracteres, cuando la base lo impone. */
  largoMaximo?: number;
  /**
   * Genera un valor para el campo. Si se indica, aparece un botón al lado y,
   * al crear un registro nuevo, el campo llega ya relleno: un token que hay
   * que inventarse a mano acaba siendo «12345678».
   */
  generar?: () => string;
  /**
   * Ocupa la fila entera del formulario en vez de media.
   *
   * Se pone a mano cuando el contenido lo pide —una descripción, una lista de
   * opciones con nombres largos—; las casillas y los campos con botón ya la
   * ocupan solos.
   */
  ancho?: 'medio' | 'completo';
  /**
   * Campos que se vacían cuando éste cambia.
   *
   * Es lo que hace posible una cascada cliente → área → nodo: al cambiar de
   * cliente, el área y el nodo que había elegidos dejan de tener sentido y
   * quedarse con ellos guardaría un sensor colgado del nodo equivocado.
   */
  limpiaAlCambiar?: readonly string[];
  /**
   * Valor con el que llega el campo en un registro nuevo.
   *
   * Para lo que tiene una respuesta razonable casi siempre —cada cuántos
   * segundos se lee un sensor— y que dejar en blanco sólo consigue que quien
   * da de alta el equipo tenga que inventárselo.
   */
  inicial?: string | number | boolean;
}

interface PaginaCrudProps<T> {
  entidad: NombreEntidad;
  titulo: string;
  descripcion: string;
  /** Texto del estado vacío cuando no hay ningún registro. */
  textoVacio: string;
  /** Convierte la fila cruda en el objeto que usan las columnas. */
  mapear: (fila: FilaInventario) => T;
  columnas: readonly Columna<T>[];
  claveFila: (item: T) => string | number;
  /** Texto por el que se busca. */
  textoBusqueda: (item: T) => string;
  campos: readonly CampoFormulario[];
  /** Prepara los valores del formulario a partir de la fila existente. */
  aFormulario: (fila: FilaInventario) => FilaInventario;
  /** Convierte los valores del formulario en lo que espera la tabla. */
  aRegistro: (valores: FilaInventario) => FilaInventario;
  /** Identificador de la fila para editar y borrar. */
  idDeFila: (fila: FilaInventario) => string | number;
  /** Filtros propios de la pantalla, sobre la consulta. */
  filtrosConsulta?: Record<string, string | number | null | undefined>;
  /** Contenido extra en la barra de filtros. */
  filtrosExtra?: ReactNode;
  /** Aviso al borrar; debe explicar la consecuencia. */
  textoBorrado: (item: T) => string;
  /** Comprobación adicional antes de guardar. */
  validar?: (valores: FilaInventario) => string | null;
  /** Botones propios de la pantalla, delante de ver, editar y eliminar. */
  accionesExtra?: (item: T) => ReactNode;
  /** Encabezado de la ficha de consulta. Por defecto, el título en singular. */
  tituloFicha?: (item: T) => string;
  /**
   * Se ejecuta después de guardar, con la clave del registro y los valores
   * del formulario.
   *
   * Para lo que no cabe en la tabla principal: un nodo guarda su nombre y su
   * área en `nodos`, pero cada cuánto debe dar señal de vida vive en
   * `nodos_runtime`. Si falla, el guardado ya está hecho y se avisa aparte:
   * no tiene sentido dar por perdido un nodo creado porque no se pudo
   * escribir un ajuste secundario.
   */
  alGuardar?: (id: string | number, valores: FilaInventario) => Promise<void>;
}

/**
 * Aplica un valor nuevo y vacía los campos que dependían del anterior.
 *
 * Vive fuera del componente porque no necesita nada de él y así se lee de un
 * vistazo qué le pasa a la cascada cuando alguien cambia de cliente.
 */
function cambiarValor(
  valores: FilaInventario,
  campo: CampoFormulario,
  nuevo: string,
): FilaInventario {
  if (valores[campo.clave] === nuevo) return valores;

  const siguiente: FilaInventario = { ...valores, [campo.clave]: nuevo };
  for (const dependiente of campo.limpiaAlCambiar ?? []) siguiente[dependiente] = '';
  return siguiente;
}

/**
 * Pantalla de mantenimiento de una tabla del inventario.
 *
 * Las pantallas de clientes, áreas, nodos, sensores y actuadores hacen lo
 * mismo: listar, buscar, crear, editar y borrar. Lo que cambia son las
 * columnas y los campos, así que se declaran y esta plantilla se ocupa del
 * resto, incluidos permisos, confirmaciones y manejo de errores.
 */
export function PaginaCrud<T>({
  entidad,
  titulo,
  descripcion,
  textoVacio,
  mapear,
  columnas,
  claveFila,
  textoBusqueda,
  campos,
  aFormulario,
  aRegistro,
  idDeFila,
  filtrosConsulta,
  filtrosExtra,
  textoBorrado,
  validar,
  accionesExtra,
  tituloFicha,
  alGuardar,
}: PaginaCrudProps<T>) {
  const clienteConsultas = useQueryClient();
  const politica = useSesionStore((s) => s.politica);
  const puedeGestionar = politica?.puede('gestionar_inventario') ?? false;
  const sinPermiso = puedeGestionar ? null : 'Tu rol no puede modificar el inventario';

  const [busqueda, setBusqueda] = useState('');
  const [formularioAbierto, setFormularioAbierto] = useState(false);
  const [filaEnEdicion, setFilaEnEdicion] = useState<FilaInventario | null>(null);
  const [valores, setValores] = useState<FilaInventario>({});
  const [error, setError] = useState<string | null>(null);

  // Registro cuya ficha de consulta está abierta.
  const [enFicha, setEnFicha] = useState<T | null>(null);

  const consulta = useQuery({
    queryKey: ['inventario', entidad, filtrosConsulta],
    queryFn: () => repositorioInventario.listar(entidad, filtrosConsulta),
  });

  const refrescar = () => {
    void clienteConsultas.invalidateQueries({ queryKey: ['inventario', entidad] });
  };

  const guardar = useMutation({
    mutationFn: async (registro: FilaInventario) => {
      let id: string | number | null;

      if (filaEnEdicion) {
        id = idDeFila(filaEnEdicion);
        await repositorioInventario.actualizar(entidad, id, registro);
      } else {
        id = await repositorioInventario.crear(entidad, registro);
      }

      if (alGuardar && id !== null) await alGuardar(id, valores);
    },
    onSuccess: () => {
      toast.success(filaEnEdicion ? 'Cambios guardados' : 'Registro creado');
      setFormularioAbierto(false);
      setFilaEnEdicion(null);
      refrescar();
    },
    onError: (e) => {
      registrarError(`inventario.${entidad}.guardar`, e);
      toast.error(e instanceof ErrorApp ? e.message : 'No se pudo guardar');
    },
  });

  const eliminar = useMutation({
    mutationFn: (id: string | number) => repositorioInventario.eliminar(entidad, id),
    onSuccess: () => {
      toast.success('Registro eliminado');
      refrescar();
    },
    onError: (e) => {
      registrarError(`inventario.${entidad}.eliminar`, e);
      toast.error(e instanceof ErrorApp ? e.message : 'No se pudo eliminar');
    },
  });

  const filas = useMemo(() => consulta.data ?? [], [consulta.data]);
  const items = useMemo(() => filas.map(mapear), [filas, mapear]);

  const filtrados = useMemo(() => {
    const t = busqueda.trim().toLowerCase();
    if (!t) return items;
    return items.filter((item) => textoBusqueda(item).toLowerCase().includes(t));
  }, [items, busqueda, textoBusqueda]);

  const abrirNuevo = () => {
    setFilaEnEdicion(null);

    /*
     * El formulario no abre vacío del todo: los campos que saben generarse
     * solos llegan rellenos —la diferencia entre un token imposible de adivinar
     * y uno escrito a mano con prisa— y los que tienen un valor razonable de
     * partida llegan con él puesto.
     */
    const iniciales: FilaInventario = {};
    for (const campo of campos) {
      if (campo.generar) iniciales[campo.clave] = campo.generar();
      else if (campo.inicial !== undefined) iniciales[campo.clave] = campo.inicial;
    }
    setValores(iniciales);
    setError(null);
    setFormularioAbierto(true);
  };

  const abrirEdicion = (fila: FilaInventario) => {
    setFilaEnEdicion(fila);
    setValores(aFormulario(fila));
    setError(null);
    setFormularioAbierto(true);
  };

  const enviar = () => {
    setError(null);

    for (const campo of campos) {
      if (!campo.requerido) continue;

      const valor = valores[campo.clave];
      if (valor === undefined || valor === null || valor === '') {
        setError(`Falta completar «${campo.etiqueta}».`);
        return;
      }
    }

    const problema = validar?.(valores);
    if (problema) {
      setError(problema);
      return;
    }

    guardar.mutate(aRegistro(valores));
  };

  /** Columna de acciones, añadida a las que declara cada pantalla. */
  const columnasConAcciones: Columna<T>[] = [
    ...columnas,
    {
      clave: '__acciones',
      encabezado: 'Acciones',
      alinear: 'centro',
      movil: 'accion',
      render: (item) => {
        const fila = filas[items.indexOf(item)];
        if (!fila) return null;

        return (
          <div className={estilos.acciones}>
            {accionesExtra?.(item)}

            <Button
              tamano="sm"
              variante="secundario"
              icono="ver"
              title="Ver datos"
              onClick={() => setEnFicha(item)}
            />
            <Button
              tamano="sm"
              variante="secundario"
              icono="editar"
              title="Editar"
              motivoDeshabilitado={sinPermiso}
              onClick={() => abrirEdicion(fila)}
            />
            <Button
              tamano="sm"
              variante="secundario"
              icono="eliminar"
              title="Eliminar"
              motivoDeshabilitado={sinPermiso}
              onClick={() => {
                if (window.confirm(textoBorrado(item))) eliminar.mutate(idDeFila(fila));
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className={estilos.pagina}>
      <CabeceraPagina
        titulo={titulo}
        descripcion={descripcion}
        acciones={
          <>
            <Button
              variante="secundario"
              icono="refrescar"
              compactoEnMovil
              onClick={refrescar}
              cargando={consulta.isFetching}
            >
              Actualizar
            </Button>
            <Button
              icono="agregar"
              compactoEnMovil
              motivoDeshabilitado={sinPermiso}
              onClick={abrirNuevo}
            >
              Nuevo
            </Button>
          </>
        }
      />

      <BarraFiltros>
        <div style={{ minWidth: 220, flex: 1 }}>
          <Input
            etiqueta="Buscar"
            icono="buscar"
            placeholder="Escribe para filtrar"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>
        {filtrosExtra}
      </BarraFiltros>

      {consulta.isLoading ? (
        <ListaEsqueleto filas={6} />
      ) : consulta.isError ? (
        <EstadoError
          acciones={
            <Button variante="secundario" icono="refrescar" onClick={refrescar}>
              Reintentar
            </Button>
          }
        />
      ) : filtrados.length === 0 ? (
        <EstadoVacio
          icono="inventario"
          titulo={items.length === 0 ? 'Sin registros' : 'Ninguno coincide'}
          descripcion={items.length === 0 ? textoVacio : 'Prueba con otra búsqueda.'}
          acciones={
            items.length === 0 ? (
              <Button icono="agregar" motivoDeshabilitado={sinPermiso} onClick={abrirNuevo}>
                Crear el primero
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Tabla
          datos={filtrados}
          columnas={columnasConAcciones}
          claveFila={claveFila}
          etiqueta={titulo}
        />
      )}

      <Modal
        abierto={formularioAbierto}
        onCerrar={() => setFormularioAbierto(false)}
        titulo={filaEnEdicion ? `Editar · ${titulo}` : `Nuevo · ${titulo}`}
        /*
         * Con muchos campos el diálogo se ensancha para caber en tres
         * columnas; con pocos se queda estrecho, que un formulario de dos
         * campos repartido a lo ancho de la pantalla se lee peor, no mejor.
         */
        ancho={campos.length > 6}
        pie={
          <>
            <Button variante="secundario" onClick={() => setFormularioAbierto(false)}>
              Cancelar
            </Button>
            <Button onClick={enviar} cargando={guardar.isPending} icono="guardar">
              Guardar
            </Button>
          </>
        }
      >
        <div className={estilos.formulario}>
          {error && <div className={estilos.error}>{error}</div>}

          <RejillaCampos>
            {campos.map((campo) => {
              const deshabilitado = campo.soloAlCrear && filaEnEdicion !== null;
              const valor = valores[campo.clave];

              if (campo.tipo === 'checkbox') {
                /*
                 * La casilla ocupa la fila entera salvo que pida media: lleva
                 * su explicación al lado y con textos largos, apretada en una
                 * columna, se parte en cinco renglones.
                 */
                const casilla = (
                  <label className={estilos.opcion}>
                    <input
                      type="checkbox"
                      checked={valor === true}
                      disabled={deshabilitado}
                      onChange={(e) =>
                        setValores((v) => ({ ...v, [campo.clave]: e.target.checked }))
                      }
                    />
                    <span>
                      <strong>{campo.etiqueta}</strong>
                      {campo.ayuda && <span className={estilos.ayuda}>{campo.ayuda}</span>}
                    </span>
                  </label>
                );

                return campo.ancho === 'medio' ? (
                  <div key={campo.clave}>{casilla}</div>
                ) : (
                  <CampoCompleto key={campo.clave}>{casilla}</CampoCompleto>
                );
              }

              if (campo.tipo === 'select') {
                const opciones =
                  typeof campo.opciones === 'function'
                    ? campo.opciones(valores)
                    : (campo.opciones ?? []);

                const desplegable = (
                  <Select
                    etiqueta={campo.etiqueta}
                    requerido={campo.requerido}
                    ayuda={campo.ayuda}
                    disabled={deshabilitado}
                    placeholder={campo.placeholder ?? 'Elige una opción'}
                    value={typeof valor === 'string' || typeof valor === 'number' ? valor : ''}
                    onChange={(e) => setValores((v) => cambiarValor(v, campo, e.target.value))}
                    opciones={opciones}
                  />
                );

                return campo.ancho === 'completo' ? (
                  <CampoCompleto key={campo.clave}>{desplegable}</CampoCompleto>
                ) : (
                  <div key={campo.clave}>{desplegable}</div>
                );
              }

              const entrada = (
                <Input
                  etiqueta={campo.etiqueta}
                  requerido={campo.requerido}
                  ayuda={campo.ayuda}
                  disabled={deshabilitado}
                  placeholder={campo.placeholder}
                  maxLength={campo.largoMaximo}
                  type={campo.tipo === 'numero' ? 'number' : 'text'}
                  value={typeof valor === 'string' || typeof valor === 'number' ? valor : ''}
                  onChange={(e) => setValores((v) => cambiarValor(v, campo, e.target.value))}
                />
              );

              if (!campo.generar || deshabilitado) {
                return campo.ancho === 'completo' ? (
                  <CampoCompleto key={campo.clave}>{entrada}</CampoCompleto>
                ) : (
                  <div key={campo.clave}>{entrada}</div>
                );
              }

              const generar = campo.generar;

              // Campo con botón de generar: media fila no le da para nada.
              return (
                <CampoCompleto key={campo.clave}>
                  <div className={estilos.campoConBoton}>
                    {entrada}
                    <Button
                      variante="secundario"
                      icono="refrescar"
                      title={`Generar un ${campo.etiqueta.toLowerCase()} nuevo`}
                      onClick={() => setValores((v) => ({ ...v, [campo.clave]: generar() }))}
                    />
                  </div>
                </CampoCompleto>
              );
            })}
          </RejillaCampos>
        </div>
      </Modal>

      <FichaRegistro
        item={enFicha}
        titulo={enFicha && tituloFicha ? tituloFicha(enFicha) : titulo}
        columnas={columnas}
        onCerrar={() => setEnFicha(null)}
      />
    </div>
  );
}
